import type { FC, ReactNode } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import Star from '@crehana/compass.icons.fill/Star';
import Certificate from '@crehana/compass.icons.outline/Certificate';
import Clock from '@crehana/compass.icons.outline/Clock';
import Slash from '@crehana/compass.icons.outline/Slash';
import { AlertCircle } from '@crehana/compass.icons.outline/v2/alert-circle';
import { cn } from '@crehana/compass.ui/utils';
import { useTranslation } from '@crehana/i18n';
import { CustomNextImage } from '@crehana/next';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Clamp, Col, Container, colors, Row } from '@crehana/ui';
import { date } from '@crehana/utils';
import { useCoursePreviewContext, useUITheme } from '@crehana/web';

import { StudioCourseConfigStateEnum } from '@/shared/types/graphql/globals';

import SkeletonAnimated from '../SkeletonAnimated';
import { PreviewVideo } from './components';
import {
  CourseTag,
  LevelCourse,
  WrapperMetrics,
} from './HeaderPreviewCourse.styles';
import { useCourseDetail } from './hooks';
import { ParseMetricsCourse } from './utils';

const { grayMain } = colors;

interface HeaderPreviewCourseProps {
  className?: string;
  amplitudeEvent?: string;
  actions?: ReactNode;
}

const HeaderPreviewCourse: FC<HeaderPreviewCourseProps> = ({
  className,
  amplitudeEvent,
  actions,
}) => {
  const { isDark } = useUITheme();
  const { t } = useTranslation();
  const { course, isLoading } = useCourseDetail();
  const { openCoursePreview } = useCoursePreviewContext();
  const { logEvent } = useAmplitude();

  const bgColor = `bg-${isDark ? 'base-dark' : 'white'}`;

  if (isLoading) {
    return (
      <div className={`${className} ${bgColor}`}>
        <Container type="dashboard" className={bgColor}>
          <Row>
            <Col md={3}>
              <SkeletonAnimated className="w-full h-full rounded-10" />
            </Col>
            <Col md={7}>
              <SkeletonAnimated className="h-24 w-full mb-8" />
              <SkeletonAnimated className="h-40 w-full mb-20" />
              <div className="flex items-center pb-20">
                <SkeletonAnimated className="w-32 h-32 min-w-32 min-h-32 rounded-full" />
                <SkeletonAnimated className="h-16 w-full ml-12" />
                <span className="mx-8">|</span>
                <SkeletonAnimated className="h-16 w-full" />
              </div>
              <WrapperMetrics isDark={isDark} isLoading>
                <SkeletonAnimated className="w-full max-w-72 h-24" />
                <SkeletonAnimated className="w-full max-w-72 h-24 ml-20" />
                <SkeletonAnimated className="w-full max-w-96 h-24 ml-20" />
                <SkeletonAnimated className="w-full max-w-96 h-24 ml-20" />
              </WrapperMetrics>
            </Col>
            <Col md={2}>
              <SkeletonAnimated />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (!course) return <></>;

  const {
    courseTag,
    duration,
    level,
    professor,
    score,
    shortDescriptionWithoutHtml,
    title,
    studioConfigSet,
  } = course;

  const hasCertificationAvailable =
    studioConfigSet?.hasCertificationAvailable ?? true;

  const isMandatory = studioConfigSet?.isMandatory ?? false;

  const isCourseHiddenState =
    studioConfigSet?.visibilityStateEnum === StudioCourseConfigStateEnum.HIDDEN;

  const amplitudePreviewCourse = () => {
    if (amplitudeEvent) {
      logEvent({
        event: amplitudeEvent,
        data: {
          ...getSourceEventParams(),
          'Course Name': title,
        },
      });
    }
  };

  const getCourseLevel = () => {
    if (level?.slug === 'introductorio') {
      return 1;
    }
    if (level?.slug === 'intermedio') {
      return 2;
    }
    return 3;
  };

  return (
    <div className={`${className} ${bgColor}`}>
      <Container type="dashboard">
        <Row>
          <Col md={3}>
            <PreviewVideo
              course={course}
              onOpenPreview={() => {
                amplitudePreviewCourse();

                openCoursePreview({
                  isDark,
                  course: {
                    ...ParseMetricsCourse({
                      ...course,
                    }),
                  },
                });
              }}
            />
          </Col>
          <Col md={7}>
            <div className="tw-flex tw-items-start tw-gap-1 tw-mb-0.8">
              <p className="tw-font-h4 tw-font-semibold">{title}</p>
              <div className="tw-flex tw-items-center tw-gap-0.8 tw-px-1.6 tw-border-solid tw-border-neutral-light-400 tw-border-l">
                <div
                  className={cn(
                    'tw-w-1 tw-h-1 tw-rounded-full',
                    isCourseHiddenState
                      ? 'tw-bg-neutral-light-600'
                      : 'tw-bg-success-400',
                  )}
                />
                <span className="tw-font-label1 tw-text-neutral-light-700">
                  {isCourseHiddenState
                    ? t('HEADER_PREVIEW_INACTIVE')
                    : t('HEADER_PREVIEW_ACTIVE')}
                </span>
              </div>
            </div>
            <Clamp clamp={2} className="tw-font-body4 tw-mb-2">
              {shortDescriptionWithoutHtml}
            </Clamp>
            {professor && (
              <div className="tw-flex tw-items-center tw-pb-2">
                <CustomNextImage
                  className="tw-object-cover tw-h-3.2 tw-w-3.2 tw-rounded-full"
                  src={`${professor.profilePictureUrl}?h=30&w=30&fit=crop&crop=faces`}
                  alt={professor.username}
                  width={32}
                  height={32}
                />
                <span className="tw-font-subtitle4 tw-ml-1.2">
                  {professor.fullName}
                </span>
                {professor.occupation && (
                  <div className="tw-font-body4">
                    <span className="tw-mx-0.8">|</span>
                    <span>{professor.occupation}</span>
                  </div>
                )}
              </div>
            )}
            <WrapperMetrics isDark={isDark}>
              {courseTag && (
                <CourseTag className="tw-font-caption">
                  {courseTag?.textLabel}
                </CourseTag>
              )}
              {score && score?.average ? (
                <div className="tw-flex tw-items-center">
                  <Star size={16} className="tw-mr-0.8 tw-text-warning-500" />
                  <span className="tw-font-body4 tw-mr-0.4">
                    {score?.average || 0}
                  </span>
                  <span className="tw-font-body4">{`(${
                    score?.total || 0
                  })`}</span>
                </div>
              ) : null}
              <div className="tw-flex tw-items-center">
                <Clock
                  size={16}
                  className="tw-mr-0.8 tw-text-primary-light-500"
                />
                <span className="tw-font-body4 tw-mr-0.4">
                  <b>{t('HEADER_PREVIEW_DURATION')}</b>
                </span>
                <span className="tw-font-body4">
                  {date.toMinutes(duration || 0)}
                </span>
              </div>

              {level && (
                <div className="tw-flex tw-items-center tw-border-solid tw-border-neutral-light-400 tw-border-x tw-px-1.6">
                  <LevelCourse
                    level={getCourseLevel()}
                    size={14}
                    isDark={isDark}
                    color={grayMain}
                    className="tw-mr-0.8"
                  />
                  <span className="tw-font-body4 tw-mr-0.2">
                    <b>{t('HEADER_PREVIEW_LEVEL')}</b>
                  </span>
                  <span className="tw-font-body4">{level?.name}</span>
                </div>
              )}

              <div className="tw-flex tw-items-center tw-gap-0.4 tw-border-solid tw-border-neutral-light-400 tw-border-r tw-pr-1.6">
                <AlertCircle size={16} className="tw-text-primary-light-500" />
                <span className="tw-font-body4 tw-mr-0.2">
                  <b>{t('HEADER_PREVIEW_MANDATORY')}</b>
                </span>
                <span className="tw-font-body4">
                  {isMandatory
                    ? t('HEADER_PREVIEW_MANDATORY_YES')
                    : t('HEADER_PREVIEW_MANDATORY_NO')}
                </span>
              </div>

              {hasCertificationAvailable ? (
                <div className="tw-flex tw-items-center tw-gap-0.4">
                  <Certificate
                    size={16}
                    className="tw-text-primary-light-400"
                  />
                  <span className="tw-font-caption tw-text-primary-light-400">
                    {t('HEADER_PREVIEW_CERTIFICATION_AVAILABLE')}
                  </span>
                </div>
              ) : (
                <div className="tw-flex tw-items-center tw-gap-0.4">
                  <Slash size={16} className="tw-text-neutral-light-700" />
                  <span className="tw-font-caption tw-text-neutral-light-700">
                    {t('HEADER_PREVIEW_CERTIFICATION_UNAVAILABLE')}
                  </span>
                </div>
              )}
            </WrapperMetrics>
          </Col>
          <Col md={2} className="flex justify-end">
            {actions}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HeaderPreviewCourse;
